// Reusable seat-quota banner + upgrade prompt. Used on Employees page.
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Sparkles, AlertCircle, X, ArrowUpRight, Crown } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";

// -- Small hook: fetch seat usage. Exposes a refresh() so callers can re-query after add/invite.
export function useSeatUsage() {
  const [usage, setUsage] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get("/billing/seat-usage");
      setUsage(data);
    } catch { /* ignore — non-critical */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  return { usage, refresh: load };
}

// -- Banner shown above Employees list. Only renders when there is a real limit.
export function SeatQuotaBanner({ usage, onUpgrade }) {
  const { t } = useI18n();
  const { user } = useAuth();
  if (!usage || usage.max == null) return null; // trial or Pro → hide
  const { current, max, remaining } = usage;
  const pct = Math.min(100, Math.round((current / max) * 100));
  const isFull = remaining <= 0;
  const isNearlyFull = !isFull && remaining <= 3;

  const tint = isFull ? "hsl(0 78% 55%)" : isNearlyFull ? "hsl(30 92% 50%)" : "hsl(var(--primary))";
  const tintSoft = isFull ? "hsl(0 78% 95%)" : isNearlyFull ? "hsl(45 92% 94%)" : "hsl(var(--primary-soft))";
  const isOwner = user?.role === "owner";

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      className="card overflow-hidden mb-4"
      data-testid="seat-quota-banner"
    >
      <div className="p-4 md:p-5 flex items-center gap-4">
        <div
          className="w-11 h-11 rounded-2xl grid place-items-center shrink-0"
          style={{ background: tintSoft, color: tint }}
        >
          {isFull ? <AlertCircle size={18} strokeWidth={2} /> : <Sparkles size={18} strokeWidth={1.9} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-[15px] font-bold text-[hsl(var(--text-strong))] leading-tight">
            {isFull
              ? t("seat_banner_full_title")
              : `${current} / ${max} ${t("seat_banner_used")}`}
          </div>
          <div className="text-[12px] text-[hsl(var(--text-muted))] mt-0.5 leading-snug">
            {isFull
              ? (isOwner ? t("seat_banner_full_owner") : t("seat_banner_full_hint"))
              : isNearlyFull
                ? `${t("seat_banner_nearly_full")} · ${remaining} ${remaining === 1 ? t("seat_banner_slot_one") : t("seat_banner_slot_many")}`
                : t("seat_banner_ok")}
          </div>
        </div>
        {isOwner && (isFull || isNearlyFull) && (
          <button
            onClick={onUpgrade}
            className="btn btn-primary !h-9 !text-[12px] shrink-0"
            data-testid="seat-quota-upgrade"
          >
            <ArrowUpRight size={13} strokeWidth={2.2} /> {t("seat_upgrade_cta")}
          </button>
        )}
      </div>
      {/* Progress bar */}
      <div className="h-1.5 bg-[hsl(var(--muted))]">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="h-full"
          style={{ background: tint }}
        />
      </div>
    </motion.div>
  );
}

// -- Modal shown when a 402 seat_limit_reached response comes back
export function SeatLimitReachedModal({ open, detail, onClose }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOwner = user?.role === "owner";
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-4"
          data-testid="seat-limit-modal"
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0, y: 12 }}
            onClick={(e) => e.stopPropagation()}
            className="card w-full max-w-md p-6 md:p-8 relative overflow-hidden"
          >
            {/* Decorative glow */}
            <div
              className="absolute -top-24 -right-24 w-52 h-52 rounded-full opacity-40 pointer-events-none"
              style={{ background: "radial-gradient(circle, hsl(var(--primary-soft)) 0%, transparent 70%)" }}
            />
            <button
              onClick={onClose}
              className="absolute top-4 right-4 btn btn-ghost !h-8 !w-8 !p-0 z-10"
              aria-label={t("close")}
              data-testid="seat-limit-close"
            >
              <X size={14} />
            </button>

            <div className="w-14 h-14 rounded-3xl bg-gradient-to-br from-[hsl(45_92%_60%)] to-[hsl(30_92%_50%)] text-white grid place-items-center mb-4 shadow-[0_10px_24px_-8px_hsl(30_92%_50%_/_0.55)]">
              <Crown size={22} strokeWidth={1.9} />
            </div>

            <div className="font-display text-[22px] md:text-[24px] font-bold text-[hsl(var(--text-strong))] tracking-tight leading-tight mb-2">
              {t("seat_modal_title")}
            </div>
            <p className="text-[14px] text-[hsl(var(--text-muted))] leading-relaxed mb-4">
              {detail?.message || t("seat_modal_body_generic")}
            </p>

            {detail?.max && (
              <div className="rounded-2xl bg-[hsl(var(--muted))] p-4 mb-5 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white grid place-items-center text-[hsl(var(--primary))]">
                  <Sparkles size={14} strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold text-[hsl(var(--text-strong))] tabular-nums">
                    {detail.current} / {detail.max} {t("seat_banner_used")}
                  </div>
                  <div className="text-[11px] text-[hsl(var(--text-muted))]">
                    {t("seat_modal_upgrade_hint")}
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                onClick={onClose}
                className="btn btn-ghost"
                data-testid="seat-limit-cancel"
              >
                {t("cancel")}
              </button>
              {isOwner ? (
                <button
                  onClick={() => { onClose(); navigate("/billing"); }}
                  className="btn btn-primary"
                  data-testid="seat-limit-goto-billing"
                >
                  <ArrowUpRight size={14} strokeWidth={2.2} /> {t("seat_modal_go_billing")}
                </button>
              ) : (
                <div className="text-[12px] italic text-[hsl(var(--text-muted))] self-center">
                  {t("seat_modal_not_owner")}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Helper for callers: pass an error, get {isSeatLimit, detail}
export function parseSeatLimitError(err) {
  const d = err?.response?.data?.detail;
  if (d && typeof d === "object" && d.code === "seat_limit_reached") {
    return { isSeatLimit: true, detail: d };
  }
  return { isSeatLimit: false, detail: null };
}
